import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GetRequest } from "../../../../api/Request";
import { AnalyticsSummary } from "../storeAnalytics.types";
import { formatDate } from "../../../../util/util";
import "../styles/analyticsDashboard.scss";

const AnalyticsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  const fetchSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let url = "admin/v1/store/analytics/summary";
      const params: string[] = [];
      
      if (fromDate) {
        const fromDateUTC = new Date(fromDate).toISOString();
        params.push(`from-date=${encodeURIComponent(fromDateUTC)}`);
      }
      if (toDate) {
        const toDateUTC = new Date(toDate).toISOString();
        params.push(`to-date=${encodeURIComponent(toDateUTC)}`);
      }
      
      if (params.length > 0) {
        url += `?${params.join("&")}`;
      }
      
      const response = await GetRequest(url);
      setSummary(response.data);
    } catch (err: any) {
      setError(err.message || "Failed to fetch analytics summary");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  const handleApplyFilters = () => {
    fetchSummary();
  };

  if (loading) {
    return (
      <div className="store-analytics-dashboard">
        <div className="store-analytics-dashboard__loading">
          Loading analytics...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="store-analytics-dashboard">
        <div className="store-analytics-dashboard__error">{error}</div>
      </div>
    );
  }

  if (!summary) return null;

  return (
    <div className="store-analytics-dashboard">
      <div className="store-analytics-dashboard__header">
        <h1>Store Analytics Dashboard</h1>
        <p>Overview of store subscriptions and activity metrics</p>
      </div>

      <div className="store-analytics-dashboard__filters">
        <div className="filter-group">
          <label>From Date</label>
          <input
            type="datetime-local"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>To Date</label>
          <input
            type="datetime-local"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
        <button onClick={handleApplyFilters}>Apply Filters</button>
      </div>

      <div className="store-analytics-dashboard__stats">
        <div className="store-analytics-dashboard__stat-card store-analytics-dashboard__stat-card--primary">
          <div className="stat-label">Total Stores</div>
          <div className="stat-value">{summary.totalStores}</div>
          <div className="stat-subtitle">All active stores</div>
        </div>

        <div className="store-analytics-dashboard__stat-card store-analytics-dashboard__stat-card--success">
          <div className="stat-label">Paid Subscribers</div>
          <div className="stat-value">{summary.paidStores}</div>
          <div className="stat-subtitle">
            {summary.totalStores && summary.paidStores && summary.totalStores > 0
              ? Math.round((summary.paidStores / summary.totalStores) * 100)
              : 0}
            % of total stores
          </div>
        </div>

        <div className="store-analytics-dashboard__stat-card store-analytics-dashboard__stat-card--warning">
          <div className="stat-label">Free Stores</div>
          <div className="stat-value">{summary.freeStores}</div>
          <div className="stat-subtitle">
            {summary.totalStores && summary.freeStores && summary.totalStores > 0
              ? Math.round((summary.freeStores / summary.totalStores) * 100)
              : 0}
            % of total stores
          </div>
        </div>

        <div className="store-analytics-dashboard__stat-card store-analytics-dashboard__stat-card--info">
          <div className="stat-label">Active (7 Days)</div>
          <div className="stat-value">{summary.activeInLast7Days}</div>
          <div className="stat-subtitle">
            {summary.totalStores && summary.totalStores > 0
              ? Math.round(
                  (summary.activeInLast7Days / summary.totalStores) * 100
                )
              : 0}
            % engagement
          </div>
        </div>

        <div className="store-analytics-dashboard__stat-card store-analytics-dashboard__stat-card--info">
          <div className="stat-label">Active (30 Days)</div>
          <div className="stat-value">{summary.activeInLast30Days}</div>
          <div className="stat-subtitle">
            {summary.totalStores && summary.totalStores > 0
              ? Math.round(
                  (summary.activeInLast30Days / summary.totalStores) * 100
                )
              : 0}
            % engagement
          </div>
        </div>
      </div>

      {summary.newStoresByDate && summary.newStoresByDate.length > 0 && (
        <div className="store-analytics-dashboard__new-stores">
          <h2>New Store Registrations</h2>
          {summary.newStoresByDate.map((dateSection) => (
            <div key={dateSection._id} className="date-section">
              <div className="date-header">
                <div className="date">{formatDate(dateSection._id)}</div>
                <div className="count">{dateSection.count} stores</div>
              </div>
              <div className="stores-list">
                {dateSection.stores.map((store) => (
                  <div 
                    key={store._id} 
                    className="store-item"
                    onClick={() => navigate(`/store/${store._id}/analytics`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="store-name">{store.name}</div>
                    <div
                      className={`subscription-badge subscription-badge--${store.subscriptionType?.toLowerCase()}`}
                    >
                      {store.subscriptionType}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {summary.newStoresByDate && summary.newStoresByDate.length === 0 && (
        <div className="store-analytics-dashboard__new-stores">
          <div className="no-data">
            No new stores in the selected date range. Apply date filters to see
            new registrations.
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
